'use strict';

var _ = require('lodash');
var CriteriaValidateAndFilterService = function CriteriaValidateAndFilterService(strategyConditionDataService, serializeStrategyService) {
  var REQUIRED_DATE_TIME = ['Time & Date', 'Date'];
  var REQUIRED_LOCATION = ['Location', 'Mkt Location', 'POS Location'];
  var levelFamilies = {
    FLIGHT: ['Flight', 'Leg', 'LegCabin', 'LegClass', 'Segment', 'SegmentClass'],
    SERVICE: ['Service', 'ServiceClassPOS'],
    MARKET: ['MarketDemand', 'MarketDemandClassGroup', 'MarketDemandPOS']
  };
  var levelToFamilyMap = _.reduce(levelFamilies, function (result, levels, family) {
    _.forEach(levels, function (level) {
      result[level] = family;
    });
    return result;
  }, {});
  var restrictedDuplicateCategories = ['Product', 'Time & Date', 'Location'];

  /**
   * Returns true if passed condition or action is empty
   * @param {StrategyConditionFactory, StrategyActionFactory} criteria
   * @returns {boolean}
   */
  function isCriteriaEmpty(criteria) {
    return criteria.isEmpty();
  }
  function isCriteriaNotEmpty(criteria) {
    return !criteria.isEmpty();
  }

  /**
   * Returns true if user didn't finish filling the criteria
   * @param {StrategyConditionFactory, StrategyActionFactory} criteria
   * @returns {boolean}
   */
  function isCriteriaIncomplete(criteria) {
    if (criteria.action === 'Remove') {
      return false;
    }
    if (criteria.category === 'User Def Ref Data' && !criteria.appendices.urdFunctionType) {
      return true;
    }
    return !criteria.isEmpty() && criteria.isValueEmpty();
  }

  /**
   * Validate if given collection of conditions contains a condition from required category
   *
   * AVPRM-18976 When a rule is created which has in Primary criteria any Date or Location criteria category the message should not be shown to the user
   * @param {Array} collection
   * @returns {Boolean}
   */
  function isLocationAndDateSelected(collection) {
    return isLocationSelected(collection) && isDateSelected(collection);
  }
  function isLocationSelected(collection) {
    if (_.isArray(collection) && collection.length > 0) {
      var requiredLocationCriteria = collection.filter(function (ele) {
        return REQUIRED_LOCATION.indexOf(ele.category) !== -1 && ele.leftOperand !== null;
      });
      return requiredLocationCriteria.length > 0;
    }
    return false;
  }
  function isDateSelected(collection) {
    if (_.isArray(collection) && collection.length > 0) {
      var requiredDateTimeCriteria = collection.filter(function (ele) {
        return REQUIRED_DATE_TIME.indexOf(ele.category) !== -1 && ele.leftOperand !== null;
      });
      return requiredDateTimeCriteria.length > 0;
    }
    return false;
  }

  /**
   * Returns true if any of the conditions in primary criteria is not filled fully
   * @param {Object} rule
   * @return {Boolean}
   */
  function hasIncompletePrimaryConditions(rule) {
    return !!_.find(rule.primaryCriteria, isCriteriaIncomplete);
  }
  function getCriteriaNamesForMessage(criteria) {
    var names = _.chain(criteria).map(function (criterion) {
      return criterion.criterion;
    }).uniq().value();
    return names.join(', ').replace(/, $/, '');
  }
  function filterCriteriaOverThreshold(criteria) {
    return _.filter(criteria, function (criterion) {
      return criterion.isOperandDataQuantityOverThreshold();
    });
  }

  /**
   * Returns true if all possible items were NOT selected in criteria with multiple dropdown operand representation
   * @param {StrategyConditionFactory} criteria
   * @returns {boolean}
   */
  function hasConditionNotAllItemsSelected(condition) {
    if (condition.leftOperand && _.isArray(condition.leftOperand) && condition.operator === '=') {
      var operandData = strategyConditionDataService.getOperandData(condition.field);
      return !(_.isArray(operandData) && operandData.length === condition.leftOperand.length);
    }
    return true;
  }

  /**
   * Filters out criteria where all possible items were NOT selected from multiple selection
   * @param {Array}
   * @returns {Array}
   */
  function notAllOperandItemsSelected(conditions) {
    return _.filter(conditions, hasConditionNotAllItemsSelected);
  }
  function notEmptyConditions(conditions) {
    return _.filter(conditions, isCriteriaNotEmpty);
  }
  function collectDuplicatedConditions(conditions) {
    return _.chain(conditions).filter(function (condition) {
      return restrictedDuplicateCategories.indexOf(condition.category) !== -1;
    }).groupBy(function (condition) {
      return [condition.category, condition.criterion].join(' - ');
    }).pickBy(function (grouped) {
      return grouped.length > 1;
    }).keys().value();
  }
  function collectDuplicatedGroupConditions(groups) {
    return _.chain(groups).map(function (group) {
      return collectDuplicatedConditions(group.conditions);
    }).flatten().uniq().value();
  }
  function isRuleCrossDomain(rule) {
    return isLinkObjectCrossDomain(rule) || hasCrossDomainStrategy(rule);
  }
  function isLinkObjectCrossDomain(rule) {
    return rule.linkObject && levelToFamilyMap[rule.linkObject.level] !== levelToFamilyMap[rule.subject.level];
  }
  function hasCrossDomainStrategy(rule) {
    return rule.strategies && _.find(rule.strategies, function (strategy) {
      return hasStatementFromDifferentLevelFamily(strategy, rule);
    });
  }
  function hasStatementFromDifferentLevelFamily(strategy, rule) {
    return _.find(strategy.statements, function (statement) {
      return levelToFamilyMap[statement.level] !== levelToFamilyMap[rule.subject.level];
    });
  }
  function hasLocationCriteria(rule) {
    rule = _.cloneDeep(rule);
    return hasLocationInPrimaryCriteria(rule) || hasLocationInLinkCriteria(rule) || hasLocationInAllCustomRuleStrategies(rule) || hasLocationInQuickRuleStrategy(rule);
  }
  function hasLocationInPrimaryCriteria(rule) {
    return rule.subject.parsedConditions && isLocationSelected(rule.subject.parsedConditions);
  }
  function hasLocationInLinkCriteria(rule) {
    return rule.linkObject && rule.linkObject.parsedConditions && isLocationSelected(rule.linkObject.parsedConditions);
  }
  function hasLocationInQuickRuleStrategy(rule) {
    if (rule.isQuickRule && rule.strategies && rule.strategies.length > 0) {
      var parsedQuickRuleStrategy = serializeStrategyService.parseStrategy(rule.strategies[0]);
      if (parsedQuickRuleStrategy.groups) {
        return _.every(parsedQuickRuleStrategy.groups, function (group) {
          return isLocationSelected(group.conditions);
        });
      }
    }
    return false;
  }
  function hasLocationInAllCustomRuleStrategies(rule) {
    if (!rule.isQuickRule && rule.strategies) {
      return _.every(rule.strategies, function (strategy) {
        var parsedStrategy = serializeStrategyService.parseStrategy(strategy);
        if (parsedStrategy.groups) {
          return _.every(parsedStrategy.groups, function (group) {
            return isLocationSelected(group.conditions);
          });
        } else if (parsedStrategy.commonConditions) {
          return isLocationSelected(parsedStrategy.commonConditions);
        }
        return false;
      });
    }
    return false;
  }
  function isWhatIfAnalysisDisabled(rule) {
    if (isRuleCrossDomain(rule)) {
      return {
        disabled: true,
        reason: 'What-if cannot be triggered on cross domain rules'
      };
    } else if (!hasLocationCriteria(rule)) {
      return {
        disabled: true,
        reason: 'Location criteria is required for What-if'
      };
    } else {
      return {
        disabled: false
      };
    }
  }
  return {
    isCriteriaEmpty: isCriteriaEmpty,
    isCriteriaIncomplete: isCriteriaIncomplete,
    isLocationAndDateSelected: isLocationAndDateSelected,
    isLocationSelected: isLocationSelected,
    isDateSelected: isDateSelected,
    hasIncompletePrimaryConditions: hasIncompletePrimaryConditions,
    getCriteriaNamesForMessage: getCriteriaNamesForMessage,
    filterCriteriaOverThreshold: filterCriteriaOverThreshold,
    notAllOperandItemsSelected: notAllOperandItemsSelected,
    notEmptyConditions: notEmptyConditions,
    collectDuplicatedConditions: collectDuplicatedConditions,
    collectDuplicatedGroupConditions: collectDuplicatedGroupConditions,
    isWhatIfAnalysisDisabled: isWhatIfAnalysisDisabled
  };
};
CriteriaValidateAndFilterService.$inject = ['StrategyConditionDataService', 'SerializeStrategyService'];
module.exports = CriteriaValidateAndFilterService;